// Tenants.jsx — gestione Tenant B2B
// Route: /tenants
// Richiede: GET/POST/PUT/DELETE /api/tenants

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ModalOverlay } from '../components/UI';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const MODEL_LABELS = {
  share: 'Revenue Share',
  saas:  'SaaS Flat',
  free:  'Freemium',
  ent:   'Enterprise',
};

const MODEL_COLORS = {
  share: '#f59e0b',
  saas:  '#6366f1',
  free:  '#10b981',
  ent:   '#06b6d4',
};

// NOTE: TENANT_TYPES are domain-specific Italian category names intentionally kept as-is
const TENANT_TYPES = [
  'Museo', 'Comune', 'Studio Notarile', 'Studio Legale',
  'Studio Commercialista', 'Tour Operator', 'Hotel / Struttura Ricettiva',
  'Università / Formazione', 'Industria / Manifattura', 'Sanità', 'Altro',
];

function authHeaders() {
  return { 'Content-Type': 'application/json' };
}

// ── Modal ─────────────────────────────────────────────────────
function TenantModal({ tenant, instances, onSave, onClose }) {
  const { t } = useTranslation(['tenants', 'common']);
  const initialIds = Array.isArray(tenant?.instanceIds) && tenant.instanceIds.length > 0
    ? tenant.instanceIds
    : (tenant?.instanceId ? [tenant.instanceId] : []);
  const [form, setForm] = useState({
    name:        tenant?.name       || '',
    type:        tenant?.type       || 'Museo',
    icon:        tenant?.icon       || '🏢',
    model:       tenant?.model      || 'saas',
    instanceIds: initialIds,
    revenue:     tenant?.revenue    || '',
    notes:       tenant?.notes      || '',
  });

  function set(key, val) { setForm(f => ({ ...f, [key]: val })); }
  function toggleInstance(id) {
    setForm(f => ({
      ...f,
      instanceIds: f.instanceIds.includes(id)
        ? f.instanceIds.filter(x => x !== id)
        : [...f.instanceIds, id],
    }));
  }

  async function handleSave() {
    if (!form.name.trim()) return;
    await onSave({ ...form, instanceIds: form.instanceIds });
    onClose();
  }

  return (
    <ModalOverlay onClose={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--bg-2)', border: '1px solid var(--border)',
        borderRadius: 14, padding: 28,
        width: 'min(520px, 100%)',
        maxHeight: 'calc(100dvh - 40px)', overflowY: 'auto',
        boxShadow: '0 24px 80px rgba(0,0,0,0.5)',
      }}>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 700, marginBottom: 4 }}>
          {tenant ? t('edit') : t('onboardingTitle')}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 20 }}>
          {t('onboardingDesc')}
        </div>

        {/* Nome */}
        <Field label={t('name')}>
          <input value={form.name} onChange={e => set('name', e.target.value)}
            placeholder={t('namePlaceholder')} style={inputStyle} />
        </Field>

        {/* Istanze RAG — multi-select chip */}
        <Field label={t('instances') + ' (multi)'}>
          {instances.length === 0 ? (
            <div style={{ fontSize: 11, color: 'var(--text-3)' }}>(Nessuna istanza disponibile)</div>
          ) : (
            <div style={{
              display: 'flex', flexWrap: 'wrap', gap: 6,
              padding: 10, background: 'var(--bg-3)', border: '1px solid var(--border)',
              borderRadius: 8, minHeight: 40,
            }}>
              {instances.map(i => {
                const sel = form.instanceIds.includes(i.id);
                return (
                  <button key={i.id} type="button" onClick={() => toggleInstance(i.id)}
                    style={{
                      padding: '4px 10px', borderRadius: 16, fontSize: 11,
                      background: sel ? 'var(--accent-glow)' : 'var(--bg-2)',
                      border: `1px solid ${sel ? 'var(--accent)' : 'var(--border)'}`,
                      color: sel ? 'var(--accent)' : 'var(--text-2)',
                      cursor: 'pointer', fontFamily: 'var(--font-mono)',
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                    }}
                  >
                    <span>{i.icon || '⬡'}</span>
                    <span>{i.name}</span>
                    {sel && <span style={{ marginLeft: 2 }}>✓</span>}
                  </button>
                );
              })}
            </div>
          )}
          <div style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 4 }}>
            Seleziona una o più istanze: l'agente sceglierà autonomamente la più rilevante per la query del tenant.
          </div>
        </Field>

        {/* Tipo + Icon row */}
        <div className="grid-form-2">
          <Field label={t('type')}>
            <select value={form.type} onChange={e => set('type', e.target.value)} style={inputStyle}>
              {TENANT_TYPES.map(t => <option key={t}>{t}</option>)}
            </select>
          </Field>
          <Field label={t('icon')}>
            <input value={form.icon} onChange={e => set('icon', e.target.value)}
              maxLength={4} placeholder="🏢" style={inputStyle} />
          </Field>
        </div>

        {/* Modello + Revenue row */}
        <div className="grid-form-2">
          <Field label={t('modelLabel')}>
            <select value={form.model} onChange={e => set('model', e.target.value)} style={inputStyle}>
              {Object.entries(MODEL_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </Field>
          <Field label={t('revenueContract')}>
            <input value={form.revenue} onChange={e => set('revenue', e.target.value)}
              placeholder="Es: €490/mese" style={inputStyle} />
          </Field>
        </div>

        {/* Note */}
        <Field label={t('notes')}>
          <textarea value={form.notes} onChange={e => set('notes', e.target.value)}
            placeholder={t('notesPlaceholder')}
            rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
        </Field>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
          <button onClick={onClose} style={btnStyle}>{t('cancel', { ns: 'common' })}</button>
          <button
            onClick={handleSave}
            disabled={!form.name.trim()}
            style={{ ...btnStyle, background: 'var(--accent)', color: '#fff', opacity: form.name.trim() ? 1 : 0.5 }}
          >
            {tenant ? t('edit') : t('new')}
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: 10, fontWeight: 600, color: 'var(--text-3)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 6 }}>
        {label}
      </label>
      {children}
    </div>
  );
}

// ── Tenant Card ───────────────────────────────────────────────
function TenantCard({ tenant, instanceStats, instancesById, onEdit, onDelete, onOpenQA, onGenKey, onRevokeKey, onShowCost }) {
  const { t } = useTranslation(['tenants', 'common']);
  const modelColor = MODEL_COLORS[tenant.model] || '#6366f1';
  const docs    = instanceStats?.stats?.documents ?? instanceStats?.documents ?? tenant.stats?.documents ?? '—';
  const chunks  = instanceStats?.stats?.chunks    ?? instanceStats?.chunks    ?? tenant.stats?.chunks    ?? '—';
  const queries = instanceStats?.stats?.queries   ?? instanceStats?.queries   ?? tenant.stats?.queries   ?? '—';
  const instName = instanceStats?.name || null;

  return (
    <div style={{
      background: 'var(--bg-2)', border: '1px solid var(--border)',
      borderRadius: 14, padding: 20, position: 'relative',
      display: 'flex', flexDirection: 'column', gap: 12,
      transition: 'border-color 0.15s',
    }}
      onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--border-bright)'}
      onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ fontSize: 28, lineHeight: 1 }}>{tenant.icon || '🏢'}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {tenant.name}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>{tenant.type}</div>
        </div>
        <span style={{
          fontSize: 10, fontWeight: 600, padding: '3px 8px', borderRadius: 5,
          background: `${modelColor}20`, color: modelColor, border: `1px solid ${modelColor}40`,
          whiteSpace: 'nowrap',
        }}>
          {MODEL_LABELS[tenant.model] || tenant.model}
        </span>
      </div>

      {/* Istanze associate (multi) */}
      {Array.isArray(tenant.instanceIds) && tenant.instanceIds.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {tenant.instanceIds.map(iid => {
            const inst = instancesById ? instancesById[iid] : null;
            const label = inst ? `${inst.icon || '⬡'} ${inst.name}` : `⬡ ${iid.slice(0, 8)}…`;
            return (
              <span key={iid} style={{
                fontSize: 10, fontFamily: 'var(--font-mono)',
                background: 'var(--bg-3)', borderRadius: 12,
                padding: '3px 9px', border: '1px solid var(--border)',
                color: 'var(--text-2)',
              }}>{label}</span>
            );
          })}
        </div>
      )}

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8 }}>
        {[
          ['Docs', docs],
          ['Chunk', chunks],
          ['Query', queries],
        ].map(([k, v]) => (
          <div key={k} style={{ background: 'var(--bg-3)', borderRadius: 8, padding: '8px 10px', textAlign: 'center' }}>
            <div style={{ fontSize: 16, fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--text-1)' }}>{v}</div>
            <div style={{ fontSize: 9, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.1em', marginTop: 2 }}>{k}</div>
          </div>
        ))}
      </div>

      {/* Revenue */}
      {tenant.revenue && tenant.revenue !== '—' && (
        <div style={{ fontSize: 12, color: '#f59e0b', fontFamily: 'var(--font-mono)' }}>
          💰 {tenant.revenue}
        </div>
      )}

      {/* API Key status */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{
          flex: 1, background: 'var(--bg-3)', borderRadius: 6, padding: '5px 10px',
          fontFamily: 'var(--font-mono)', fontSize: 10,
          color: tenant.apiKey ? '#10b981' : 'var(--text-3)',
          border: `1px solid ${tenant.apiKey ? 'rgba(16,185,129,0.3)' : 'var(--border)'}`,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {tenant.protected
            ? (tenant.apiKey || t('apiKeyProtected'))
            : t('apiKeyFree')}
        </div>
        <button onClick={() => onGenKey(tenant)} title={t('generateKeyTooltip')} style={{
          ...smallBtn,
          background: 'rgba(16,185,129,0.1)', color: '#10b981',
          border: '1px solid rgba(16,185,129,0.3)',
        }}>🔑</button>
        {tenant.protected && (
          <button onClick={() => onRevokeKey(tenant.id)} title={t('revokeKeyTooltip')} style={{
            ...smallBtn, color: 'var(--red)', borderColor: 'var(--red)',
          }}>✕</button>
        )}
      </div>

      {/* Note */}
      {tenant.notes && (
        <div style={{ fontSize: 11, color: 'var(--text-3)', borderTop: '1px solid var(--border)', paddingTop: 8, lineHeight: 1.5 }}>
          {tenant.notes.substring(0, 80)}{tenant.notes.length > 80 ? '…' : ''}
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
        {Array.isArray(tenant.instanceIds) && tenant.instanceIds.length > 0 && (
          <button
            onClick={() => onOpenQA(tenant)}
            title={tenant.instanceIds.length > 1
              ? `Apre l'agente in modalità multi-tenant su ${tenant.instanceIds.length} istanze`
              : "Apre l'agente sull'istanza del tenant"}
            style={{ ...btnStyle, flex: 1, fontSize: 11, padding: '6px 10px', background: 'var(--accent)', color: '#fff', border: 'none' }}
          >
            {t('openQA')} {tenant.instanceIds.length > 1 ? `(${tenant.instanceIds.length})` : ''}
          </button>
        )}
        <button onClick={() => onShowCost(tenant)} title="Costi tenant" style={{ ...btnStyle, fontSize: 11, padding: '6px 10px', color: 'var(--green)', borderColor: 'rgba(16,185,129,0.4)' }}>
          💵
        </button>
        <button onClick={() => onEdit(tenant)} style={{ ...btnStyle, fontSize: 11, padding: '6px 10px' }}>
          ✎
        </button>
        <button onClick={() => onDelete(tenant.id)} style={{ ...btnStyle, fontSize: 11, padding: '6px 10px', color: 'var(--red)', borderColor: 'var(--red)' }}>
          ✕
        </button>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────
export default function Tenants() {
  const { t } = useTranslation(['tenants', 'common']);
  const [tenants, setTenants] = useState([]);
  const [instances, setInstances] = useState([]);
  const [instStats, setInstStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [newApiKey, setNewApiKey] = useState(null); // mostrata dopo generazione
  const [costTenant, setCostTenant] = useState(null); // tenant aperto in modal costi
  const navigate = useNavigate();

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, iRes] = await Promise.all([
        fetch(`${API_BASE}/tenants`, { headers: authHeaders(), credentials: 'include' }),
        fetch(`${API_BASE}/instances`, { headers: authHeaders(), credentials: 'include' }),
      ]);
      const tData = await tRes.json();
      const iData = await iRes.json();

      const tenantList = tData.tenants || [];
      const instList   = iData.instances || [];

      setTenants(tenantList);
      setInstances(instList);

      // Build instance stats map
      const statsMap = {};
      instList.forEach(i => { statsMap[i.id] = i; });
      setInstStats(statsMap);
    } catch (err) {
      console.error('loadAll error:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  async function handleSave(data) {
    if (editingTenant) {
      await fetch(`${API_BASE}/tenants/${editingTenant.id}`, {
        method: 'PUT', headers: authHeaders(), credentials: 'include', body: JSON.stringify(data),
      });
    } else {
      await fetch(`${API_BASE}/tenants`, {
        method: 'POST', headers: authHeaders(), credentials: 'include', body: JSON.stringify(data),
      });
    }
    setEditingTenant(null);
    loadAll();
  }

  async function handleDelete(id) {
    if (!window.confirm(t('deleteConfirm', { name: tenants.find(t => t.id === id)?.name || id }))) return;
    await fetch(`${API_BASE}/tenants/${id}`, { method: 'DELETE', headers: authHeaders(), credentials: 'include' });
    loadAll();
  }

  async function handleGenKey(tenant) {
    if (!window.confirm(t('generateKeyConfirm', { name: tenant.name }))) return;
    const res = await fetch(`${API_BASE}/tenants/${tenant.id}/gen-key`, {
      method: 'POST', headers: authHeaders(), credentials: 'include',
    });
    const data = await res.json();
    setNewApiKey({ key: data.apiKey, tenantName: tenant.name });
    loadAll();
  }

  async function handleRevokeKey(id) {
    if (!window.confirm(t('revokeKeyConfirm'))) return;
    await fetch(`${API_BASE}/tenants/${id}/revoke-key`, {
      method: 'POST', headers: authHeaders(), credentials: 'include',
    });
    loadAll();
  }

  function openNew() {
    setEditingTenant(null);
    setModalOpen(true);
  }

  function openEdit(tenant) {
    setEditingTenant(tenant);
    setModalOpen(true);
  }

  return (
    <div className="page-content animate-fade">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700 }}>
          {t('title')} <span style={{ color: 'var(--accent)' }}>B2B</span>
        </h1>
        <button onClick={openNew} style={{ ...btnStyle, background: 'var(--accent)', color: '#fff', border: 'none' }}>
          ＋ {t('new')}
        </button>
      </div>

      {/* Grid */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-3)' }}>{t('loading', { ns: 'common' })}</div>
      ) : tenants.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-3)' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>◑</div>
          <div>{t('noTenants')}</div>
          <button onClick={openNew} style={{ ...btnStyle, marginTop: 16, color: 'var(--accent)', borderColor: 'var(--accent)' }}>
            {t('new')} →
          </button>
        </div>
      ) : (
        <div className="grid-cards">
          {tenants.map(tenant => (
            <TenantCard
              key={tenant.id}
              tenant={tenant}
              instanceStats={tenant.instanceId ? instStats[tenant.instanceId] : null}
              instancesById={Object.fromEntries(instances.map(i => [i.id, i]))}
              onEdit={openEdit}
              onDelete={handleDelete}
              onOpenQA={tenantObj => {
                const ids = tenantObj.instanceIds || [];
                const qs  = new URLSearchParams({
                  instanceIds: ids.join(','),
                  tenantId:    tenantObj.id,
                  tenantName:  tenantObj.name,
                });
                navigate(`/agent?${qs.toString()}`);
              }}
              onGenKey={handleGenKey}
              onRevokeKey={handleRevokeKey}
              onShowCost={setCostTenant}
            />
          ))}
          {/* Add card */}
          <div
            onClick={openNew}
            style={{
              border: '2px dashed var(--border)', borderRadius: 14,
              minHeight: 120, display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', gap: 8,
              cursor: 'pointer', color: 'var(--text-3)',
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.color = 'var(--accent)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-3)'; }}
          >
            <span style={{ fontSize: 28 }}>＋</span>
            <span style={{ fontSize: 11, fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{t('new')}</span>
          </div>
        </div>
      )}

      {/* API Key reveal modal */}
      {newApiKey && (
        <div onClick={() => setNewApiKey(null)} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)',
          zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            width: 520, background: 'var(--bg-2)', border: '1px solid #10b981',
            borderRadius: 14, padding: 28, boxShadow: '0 0 40px rgba(16,185,129,0.2)',
          }}>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 700, marginBottom: 4 }}>
              🔑 {t('apiKey')}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 16 }}>
              {t('apiKeyRevealIntro', { name: newApiKey.tenantName })}
            </div>
            <div style={{
              background: 'var(--bg-3)', border: '1px solid #10b981', borderRadius: 8,
              padding: '12px 14px', fontFamily: 'var(--font-mono)', fontSize: 12,
              color: '#10b981', wordBreak: 'break-all', lineHeight: 1.6, marginBottom: 16,
            }}>
              {newApiKey.key}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 16 }}>
              Per interrogare l'istanza protetta usa l'header:<br/>
              <code style={{ color: '#f59e0b' }}>x-api-key: {newApiKey.key}</code><br/>
              oppure il parametro query: <code style={{ color: '#f59e0b' }}>?api_key=...</code>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => { navigator.clipboard.writeText(newApiKey.key); }}
                style={{ ...btnStyle, color: '#10b981', borderColor: '#10b981' }}>
                📋 {t('copy', { ns: 'common' })}
              </button>
              <button onClick={() => setNewApiKey(null)} style={{ ...btnStyle, background: '#10b981', color: '#000', border: 'none' }}>
                {t('close', { ns: 'common' })} ✓
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal */}
      {modalOpen && (
        <TenantModal
          tenant={editingTenant}
          instances={instances}
          onSave={handleSave}
          onClose={() => { setModalOpen(false); setEditingTenant(null); }}
        />
      )}

      {/* Cost modal */}
      {costTenant && (
        <TenantCostModal tenant={costTenant} onClose={() => setCostTenant(null)} />
      )}
    </div>
  );
}

// ── Tenant Cost Modal ────────────────────────────────────────────
function TenantCostModal({ tenant, onClose }) {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetch(`${API_BASE}/tenants/${tenant.id}/cost?days=${days}`, {
      headers: authHeaders(), credentials: 'include',
    })
      .then(r => r.json())
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [tenant.id, days]);

  const total = data?.total || { usd: 0, tokens: 0, calls: 0 };
  return (
    <ModalOverlay onClose={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--bg-2)', border: '1px solid var(--border)',
        borderRadius: 14, padding: 28,
        width: 'min(820px, 100%)', maxHeight: 'calc(100dvh - 40px)', overflowY: 'auto',
        boxShadow: '0 24px 80px rgba(0,0,0,0.5)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
          <div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700 }}>
              💵 Costi tenant — {tenant.name}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4 }}>
              Spesa AI per chiamate con API key di questo tenant
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-3)', cursor: 'pointer', fontSize: 20 }}>✕</button>
        </div>

        {/* Range buttons */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
          {[1, 7, 30, 90, 365].map(n => (
            <button key={n} onClick={() => setDays(n)} style={{
              padding: '6px 12px', fontSize: 11, fontFamily: 'var(--font-mono)',
              background: days === n ? 'var(--accent-glow)' : 'var(--bg-3)',
              border: `1px solid ${days === n ? 'var(--accent)' : 'var(--border)'}`,
              color: days === n ? 'var(--accent)' : 'var(--text-2)',
              borderRadius: 6, cursor: 'pointer',
            }}>{n === 365 ? '1y' : `${n}d`}</button>
          ))}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-3)' }}>Caricamento…</div>
        ) : data?.error ? (
          <div style={{ color: 'var(--red)', fontSize: 12 }}>Errore: {data.error}</div>
        ) : total.calls === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-3)', fontSize: 12 }}>
            Nessuna chiamata AI con la API key di questo tenant negli ultimi {days} giorni
          </div>
        ) : (
          <>
            {/* Totali */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 16 }}>
              <CostStat label="Totale USD" value={`$${total.usd.toFixed(4)}`} color="#10b981" />
              <CostStat label="Token totali" value={Number(total.tokens).toLocaleString()} color="#06b6d4" />
              <CostStat label="Chiamate LLM" value={total.calls} color="var(--accent)" />
            </div>

            {/* Per istanza */}
            <div style={{ marginBottom: 14 }}>
              <h4 style={{ fontFamily: 'var(--font-display)', fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Per istanza</h4>
              <CostBars rows={data.byInstance || []} keyField="instanceName" />
            </div>
            {/* Per modello */}
            <div style={{ marginBottom: 14 }}>
              <h4 style={{ fontFamily: 'var(--font-display)', fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Per modello</h4>
              <CostBars rows={data.byModel || []} keyField="model" />
            </div>
            {/* Per ruolo */}
            <div>
              <h4 style={{ fontFamily: 'var(--font-display)', fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Per ruolo (router/critic/synth/…)</h4>
              <CostBars rows={data.byRole || []} keyField="role" />
            </div>
          </>
        )}
      </div>
    </ModalOverlay>
  );
}

function CostStat({ label, value, color }) {
  return (
    <div style={{ background: 'var(--bg-3)', borderRadius: 10, padding: '14px 16px', border: '1px solid var(--border)' }}>
      <div style={{ fontSize: 10, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>{label}</div>
      <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 22, color, lineHeight: 1 }}>{value}</div>
    </div>
  );
}

function CostBars({ rows, keyField }) {
  if (!rows.length) return <div style={{ fontSize: 11, color: 'var(--text-3)' }}>—</div>;
  const max = Math.max(...rows.map(r => r.usd || 0), 0.000001);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {rows.map((r, i) => {
        const pct = (r.usd / max) * 100;
        return (
          <div key={i}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3, fontFamily: 'var(--font-mono)', fontSize: 11 }}>
              <span style={{ color: 'var(--text-1)', fontWeight: 600 }}>{r[keyField]}</span>
              <span style={{ color: 'var(--text-3)' }}>${r.usd.toFixed(4)} · {Number(r.tokens).toLocaleString()}tok · {r.calls}x</span>
            </div>
            <div style={{ height: 5, background: 'var(--bg-3)', borderRadius: 3, overflow: 'hidden' }}>
              <div style={{ width: pct + '%', height: '100%', background: `hsl(${i * 47 % 360}, 70%, 60%)`, transition: 'width 0.4s' }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

const btnStyle = {
  padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border)',
  background: 'var(--bg-3)', color: 'var(--text-1)', fontFamily: 'var(--font-mono)',
  fontSize: 12, cursor: 'pointer',
};

const smallBtn = {
  padding: '4px 8px', borderRadius: 6, border: '1px solid var(--border)',
  background: 'var(--bg-3)', cursor: 'pointer', fontSize: 12, flexShrink: 0,
};

const inputStyle = {
  width: '100%', background: 'var(--bg-3)', border: '1px solid var(--border)',
  borderRadius: 8, padding: '9px 12px', color: 'var(--text-1)',
  fontFamily: 'var(--font-mono)', fontSize: 12, outline: 'none',
};
